import React from "react";
import {
  Box,
  Typography,
  TextField,
  TextFieldProps,
  TypographyProps,
  styled,
  Paper,
  keyframes,
  Checkbox,
  FormControlLabel
} from "@mui/material";
import { Dashboard as DashboardIcon } from "@mui/icons-material";

// Animation keyframes
const slideIn = keyframes`
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

// Styled components
const SettingCard = styled(Paper)(({ theme }) => ({
  animation: `${slideIn} 0.3s ease-out`,
  transition: "all 0.2s ease",
  "&:hover": {
    transform: "translateY(-2px)",
  },
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
}));

type SettingRow = {
  label: string;
  value: number | boolean; // Updated to support boolean
  unit: string;
  key: string;
  type: string;
  inputProps?: TextFieldProps;
  typographyProps?: TypographyProps;
};

type ReusableSettingsCardProps = {
  icon?: React.ReactNode;
  title: string;
  settings: SettingRow[];
  onChange: (key: string, value: number | string | boolean) => void; // Added boolean support
};

const ReusableSettingsCard = ({
                                icon = <DashboardIcon />,
                                title,
                                settings,
                                onChange,
                              }: ReusableSettingsCardProps) => {
  const handleChange =
      (settingType: string, key: string) =>
          (e: React.ChangeEvent<HTMLInputElement>) => {
            if (settingType === "boolean") {
              onChange(key, e.target.checked);
            } else {
              const value =
                  settingType === "number"
                      ? parseInt(e.target.value, 10)
                      : e.target.value;
              onChange(key, value);
            }
          };

  return (
      <Box>
        <SettingCard elevation={3}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            {React.cloneElement(icon as React.ReactElement, { sx: { mr: 1 } })}
            <Typography variant="h6">{title}</Typography>
          </Box>

          {settings.map((setting) => (
              <Box key={setting.key} sx={{ mb: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Typography
                      variant="body1"
                      sx={{ minWidth: 200 }}
                      {...setting.typographyProps}
                  >
                    {setting.label}
                  </Typography>

                  {setting.type === "boolean" ? (
                      <Box sx={{ width: 350, display: 'flex' }}>
                        <FormControlLabel
                            control={
                              <Checkbox
                                  checked={Boolean(setting.value)}
                                  onChange={handleChange("boolean", setting.key)}
                                  color="primary"
                              />
                            }
                            label={Boolean(setting.value) ? "Enabled" : "Disabled"}
                            sx={{ marginLeft: '-11px' }} // Adjust alignment
                        />
                      </Box>
                  ) : (
                      <TextField
                          type={setting.type}
                          value={setting.value}
                          onChange={handleChange(setting.type, setting.key)}
                          sx={{ width: 350 }}
                          variant="outlined"
                          size="small"
                          inputProps={{
                            ...setting.inputProps?.inputProps,
                          }}
                          {...setting.inputProps}
                      />
                  )}

                  {setting.type !== "boolean" && (
                      <Typography variant="body1">{setting.unit}</Typography>
                  )}
                </Box>
              </Box>
          ))}
        </SettingCard>
      </Box>
  );
};

export default ReusableSettingsCard;